import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface UseStorageOptions {
  poll?: number;
  logError?: (error: any) => void;
  updateInitialValueOnChange?: boolean;
}

export function getStorage<T>(
  storage: typeof window.localStorage | typeof window.sessionStorage,
  key: string,
  initialValue: T,
  { logError }: UseStorageOptions = {},
): T {
  try {
    const saved = storage.getItem(key);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (err) {
    logError?.(err);
  }

  return initialValue;
}

export function useStorage<T>(
  storage: typeof window.localStorage | typeof window.sessionStorage,
  key: string,
  initialValue: T,
  { poll, logError, updateInitialValueOnChange }: UseStorageOptions = {},
): [T, (value: T) => void, () => void] {
  const [storedValue, setStoredValue] = useState<T>(() =>
    getStorage(storage, key, initialValue, { logError }),
  );

  const setValue: Dispatch<SetStateAction<T>> = useCallback(
    (value) => {
      try {
        setStoredValue((prev) => {
          const valueToStore = value instanceof Function ? value(prev) : value;

          if (prev !== valueToStore) {
            storage.setItem(key, JSON.stringify(valueToStore));
          }

          return valueToStore;
        });
      } catch (error) {
        logError?.(error);
      }
    },
    [key, logError, storage],
  );

  const prevInitialValueRef = useRef(initialValue);
  useEffect(() => {
    if (updateInitialValueOnChange) {
      setStoredValue((prev) => {
        if (prev === prevInitialValueRef.current && initialValue !== prevInitialValueRef.current) {
          prevInitialValueRef.current = initialValue;
          return initialValue;
        }

        prevInitialValueRef.current = prev;
        return prev;
      });
    }
  }, [updateInitialValueOnChange, initialValue]);

  const clearValue = useCallback(() => {
    storage.removeItem(key);
    setStoredValue(initialValue);
  }, [storage, key, initialValue]);

  useEffect(() => {
    if (!poll) {
      return undefined;
    }

    const pollingFn = () => {
      const item = storage.getItem(key);
      const parsedItem = item ? JSON.parse(item) : null;

      if (parsedItem === null) {
        return;
      }

      if (JSON.stringify(storedValue) !== JSON.stringify(parsedItem)) {
        setStoredValue(parsedItem);
      }
    };

    const handle = setInterval(pollingFn, poll);

    return () => {
      clearTimeout(handle);
    };
  }, [poll, storedValue, key, storage]);

  return useMemo(() => [storedValue, setValue, clearValue], [storedValue, setValue, clearValue]);
}
